import { useAppDispatch, useAuth } from '@/store/hooks'
import { logout } from '@/store/slices/authSlice'
import {
    IDLE_LIMIT_MS,
    isSessionIdle,
    markActivity,
} from '@/lib/session-activity'
import { isTabSession } from '@/lib/auth-tab-session'
import { isAiUserType } from '@/lib/auth/restricted-viewer'
import { useEffect } from 'react'
import { toast } from 'react-toastify'
import { endServerSession } from '@/lib/api/session-refresh'

/**
 * Encerra a sessão parada. O operador que está atendendo nunca é atingido —
 * qualquer clique, tecla ou rolagem renova a marcação; quem larga a estação
 * aberta no balcão perde a sessão em {@link IDLE_LIMIT_MS}.
 *
 * Os eventos são capturados na fase de captura e passivos: eles só observam,
 * nunca interferem no comportamento da página.
 */
const ACTIVITY_EVENTS = [
    'mousedown',
    'keydown',
    'wheel',
    'touchstart',
    'scroll',
] as const

const CHECK_INTERVAL_MS = 60 * 1000

export function useIdleLogout() {
    const dispatch = useAppDispatch()
    const { isAuthenticated, user } = useAuth()
    // Usuário de IA não sai por inatividade: a aba dele fica aberta esperando
    // conversa, sem ninguém clicando (docs/ai-user.md). Também não marca atividade,
    // para que o trabalho dele não mantenha viva a sessão de quem largou outra aba.
    const aiTab = isAiUserType(user?.userType) || isTabSession()

    useEffect(() => {
        if (!isAuthenticated || aiTab) return

        // Entrar na aplicação já conta como atividade: sem esta marcação, uma
        // sessão recém-aberta herdaria o carimbo antigo da última visita.
        markActivity(true)

        const handleActivity = () => markActivity()

        ACTIVITY_EVENTS.forEach(event =>
            window.addEventListener(event, handleActivity, {
                capture: true,
                passive: true,
            })
        )

        // Voltar para a aba é interação. Sem isto, quem fica com a tela aberta
        // em outra janela por mais que o limite cairia ao voltar.
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') markActivity(true)
        }
        document.addEventListener('visibilitychange', handleVisibility)

        const timer = window.setInterval(() => {
            if (!isSessionIdle()) return

            // A sessão do servidor também cai: refresh esquecido em disco não
            // volta a valer depois do tempo de ociosidade.
            void endServerSession()
            dispatch(logout())
            toast.info(
                'Sessão encerrada por inatividade. Entre novamente para continuar.'
            )
            window.location.href = '/'
        }, CHECK_INTERVAL_MS)

        return () => {
            ACTIVITY_EVENTS.forEach(event =>
                window.removeEventListener(event, handleActivity, {
                    capture: true,
                })
            )
            document.removeEventListener('visibilitychange', handleVisibility)
            window.clearInterval(timer)
        }
    }, [isAuthenticated, aiTab, dispatch])
}
